import { useState } from "react";
import { isMetalProfile, getProfileMetal } from "../constants";
import { openExternalPage } from "../utils/navigation";

const fmt     = n => "₹" + new Intl.NumberFormat("en-IN",{maximumFractionDigits:0}).format(Math.abs(n));
const fmtFull = n => "₹" + new Intl.NumberFormat("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2}).format(Math.abs(Number(n)||0));
const toGram4 = n => { const num = Number(n || 0); return Number.isFinite(num) ? Math.round((num + Number.EPSILON) * 10000) / 10000 : 0; };
const fmtGram = n => `${toGram4(n).toFixed(4)} g`;
const MONTHS  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const autoStatus = (opening = 0, closing = 0, settledLabel = "Settled") => {
  const start = Number(opening || 0);
  const paid = Number(closing || 0);
  if (start <= 0) return "No activity";
  if (paid <= 0) return "Pending";
  if (paid < start) return "Partial";
  return settledLabel;
};


export default function Reports({ accounts, txnMap, dark, tm }) {
  const [tab, setTab] = useState("finance");

  const allStdTxns = accounts.filter(a=>a.profileType==="standard")
    .flatMap(a => txnMap[a.id]||[]);
  const allLendTxns = accounts.filter(a=>a.profileType==="lending")
    .flatMap(a => (txnMap[a.id]||[]).map(t => ({
      ...t,
      accountId: a.id,
      profileName: a.name || t.person || "Lending Profile",
      profileIcon: a.icon || "👤",
      profileColor: a.color || "#059669",
      phone: t.phone || a.phone || ""
    })));
  const allTradeTxns = accounts.filter(a=>a.profileType==="trading")
    .flatMap(a => txnMap[a.id]||[]);
  const allGoldTxns = accounts.filter(a=>isMetalProfile(a.profileType))
    .flatMap(a => (txnMap[a.id]||[]).map(t => ({ ...t, metalType: t.metalType || getProfileMetal(a.profileType) || "Gold" })));
  const allFdrdTxns = accounts.filter(a=>a.profileType==="fdrd")
    .flatMap(a => txnMap[a.id]||[]);

  // ── Finance stats
  const totalInc = allStdTxns.filter(t=>t.type==="income").reduce((s,t)=>s+t.amount,0);
  const totalExp = allStdTxns.filter(t=>t.type==="expense").reduce((s,t)=>s+t.amount,0);
  const savings  = totalInc - totalExp;

  // Monthly breakdown (last 6 months)
  const now = new Date();
  const monthlyData = Array.from({length:6}, (_,i) => {
    const d = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
    const m = d.getMonth(); const y = d.getFullYear();
    const txns = allStdTxns.filter(t => {
      const td = new Date(t.date); return td.getMonth()===m && td.getFullYear()===y;
    });
    return {
      label: MONTHS[m],
      inc: txns.filter(t=>t.type==="income").reduce((s,t)=>s+t.amount,0),
      exp: txns.filter(t=>t.type==="expense").reduce((s,t)=>s+t.amount,0),
    };
  });

  // Category breakdown
  const catMap = {};
  allStdTxns.filter(t=>t.type==="expense").forEach(t=>{
    catMap[t.category]=(catMap[t.category]||0)+t.amount;
  });
  const catEntries = Object.entries(catMap).sort((a,b)=>b[1]-a[1]);
  const catTotal   = catEntries.reduce((s,[,v])=>s+v,0);

  // ── Lending stats
  const totalLent  = allLendTxns.filter(t=>t.txType==="lent").reduce((s,t)=>s+t.amount,0);
  const totalRet   = allLendTxns.filter(t=>t.txType==="returned").reduce((s,t)=>s+t.amount,0);
  const totalBorr  = allLendTxns.filter(t=>t.txType==="borrowed").reduce((s,t)=>s+t.amount,0);
  const totalRepaid= allLendTxns.filter(t=>t.txType==="repaid").reduce((s,t)=>s+t.amount,0);
  const totalLendingCashback = allLendTxns.reduce((s,t)=>s+Number(t.cashback||0)+(t.txType==="profit"?Number(t.amount||0):0),0);
  const outstanding= Math.max(0, totalLent - totalRet);
  const youOwe     = Math.max(0, totalBorr - totalRepaid);
  const netLendingPosition = outstanding - youOwe;
  const receivableStatus = autoStatus(totalLent, totalRet, "Returned");
  const payableStatus = autoStatus(totalBorr, totalRepaid, "Repaid");

  const lendingPendingRows = Object.values(allLendTxns.reduce((acc, t) => {
    const key = t.accountId || t.person || t.profileName || "unknown";
    if (!acc[key]) {
      acc[key] = {
        key,
        name: t.profileName || t.person || "Lending Profile",
        icon: t.profileIcon || "👤",
        color: t.profileColor || "#059669",
        phone: t.phone || "",
        lent: 0, returned: 0, borrowed: 0, repaid: 0, cashback: 0, latestDueDate: "", latestNote: ""
      };
    }
    const amount = Number(t.amount || 0);
    if (t.txType === "lent") acc[key].lent += amount;
    if (t.txType === "returned") acc[key].returned += amount;
    if (t.txType === "borrowed") acc[key].borrowed += amount;
    if (t.txType === "repaid") acc[key].repaid += amount;
    acc[key].cashback += Number(t.cashback||0) + (t.txType === "profit" ? amount : 0);
    if (t.phone) acc[key].phone = t.phone;
    if (t.note) acc[key].latestNote = t.note;
    if (t.dueDate && (!acc[key].latestDueDate || t.dueDate < acc[key].latestDueDate)) acc[key].latestDueDate = t.dueDate;
    return acc;
  }, {})).map(row => {
    const toCollect = Math.max(0, row.lent - row.returned);
    const toPay = Math.max(0, row.borrowed - row.repaid);
    const net = toCollect - toPay;
    return { ...row, toCollect, toPay, net };
  }).filter(row => row.toCollect > 0 || row.toPay > 0)
    .sort((a,b) => Math.abs(b.net) - Math.abs(a.net));

  const openWhatsAppReminder = (row) => {
    if (!row.phone) return;
    const cleanPhone = String(row.phone).replace(/\D/g, "");
    if (!cleanPhone) return;
    const amount = row.toCollect > 0 ? fmtFull(row.toCollect) : fmtFull(row.toPay);
    const msg = row.toCollect > 0
      ? `Hi ${row.name}, gentle reminder for pending amount ${amount}.`
      : `Hi ${row.name}, reminder noted for amount ${amount} I need to pay.`;
    openExternalPage(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`);
  };

  // Overdue
  const today = new Date().toISOString().split("T")[0];
  const overdue = allLendTxns.filter(t =>
    t.txType==="lent" && t.dueDate && t.dueDate < today
  );

  // ── Trading stats
  const tradeProfits = allTradeTxns.filter(t=>t.txType==="profit");
  const tradeLosses  = allTradeTxns.filter(t=>t.txType==="loss");
  const totalProfit  = tradeProfits.reduce((s,t)=>s+t.amount,0);
  const totalLoss    = tradeLosses.reduce((s,t)=>s+t.amount,0);
  const netPnL       = totalProfit - totalLoss;
  const winRate      = (tradeProfits.length+tradeLosses.length) > 0
    ? Math.round((tradeProfits.length/(tradeProfits.length+tradeLosses.length))*100) : 0;
  const avgProfit    = tradeProfits.length ? totalProfit/tradeProfits.length : 0;
  const avgLoss      = tradeLosses.length  ? totalLoss/tradeLosses.length   : 0;
  const rr           = avgLoss > 0 ? (avgProfit/avgLoss).toFixed(2) : "—";

  // ── Best/Worst trades
  const bestTrade  = [...allTradeTxns].filter(t=>t.txType==="profit").sort((a,b)=>b.amount-a.amount)[0];
  const worstTrade = [...allTradeTxns].filter(t=>t.txType==="loss").sort((a,b)=>b.amount-a.amount)[0];

  // ── Gold/Silver stats
  const metalStats = ["Gold", "Silver"].map(metal => {
    const items = allGoldTxns.filter(t => (t.metalType || "Gold") === metal);
    const bought = items.filter(t=>t.txType==="buy").reduce((s,t)=>s+Number(t.grams||0),0);
    const sold = items.filter(t=>t.txType==="sell").reduce((s,t)=>s+Number(t.grams||0),0);
    const adjusted = items.filter(t=>t.txType==="adjustment").reduce((s,t)=>s+Number(t.grams||0),0);
    const buyValue = items.filter(t=>t.txType==="buy").reduce((s,t)=>s+Number(t.amount||0),0);
    const soldValue = items.filter(t=>t.txType==="sell").reduce((s,t)=>s+Number(t.amount||0),0);
    const cashback = items.filter(t=>t.txType==="buy" || t.txType==="cashback").reduce((s,t)=>s+Number(t.cashback||0)+Number(t.txType==="cashback"?t.amount||0:0),0);
    // Cashback is reported separately only; it is not deducted from purchase cost.
    const netCost = Math.max(0, buyValue);
    const avg = bought > 0 ? buyValue / bought : 0;
    const holding = bought - sold + adjusted;
    const holdingValue = Math.max(0, holding * avg);
    const realised = soldValue - (sold * avg);
    return { metal, holding, bought, sold, buyValue, soldValue, cashback, netCost, avg, holdingValue, holdingCost: holdingValue, realised };
  });
  const totalGoldSilverHolding = metalStats.reduce((s,m)=>s+m.holdingValue,0);
  const totalGoldSilverSold = metalStats.reduce((s,m)=>s+m.soldValue,0);
  const totalGoldSilverCashback = metalStats.reduce((s,m)=>s+m.cashback,0);

  // ── FD/RD stats
  const fdrdInvested = allFdrdTxns.reduce((s,t)=>s+Number(t.amount||0),0);
  const fdrdExpected = allFdrdTxns.reduce((s,t)=>s+Number(t.expectedMaturityAmount||0),0);
  const fdrdActual = allFdrdTxns.reduce((s,t)=>s+Number(t.actualReturnedAmount||0),0);
  const fdrdPenalty = allFdrdTxns.reduce((s,t)=>s+Number(t.penalty||0),0);
  const fdrdClosed = allFdrdTxns.filter(t=>!["active","renewed"].includes(t.txType||"active"));
  const fdrdClosedInvested = fdrdClosed.reduce((s,t)=>s+Number(t.amount||0),0);
  const fdrdExpectedGain = fdrdExpected - fdrdInvested;
  const fdrdActualGain = fdrdActual - fdrdClosedInvested - fdrdPenalty;
  const fdrdExpectedPct = fdrdInvested > 0 ? (fdrdExpectedGain / fdrdInvested) * 100 : 0;
  const fdrdActualPct = fdrdClosedInvested > 0 ? (fdrdActualGain / fdrdClosedInvested) * 100 : 0;
  const fdrdActive = allFdrdTxns.filter(t=>["active","renewed"].includes(t.txType||"active"));
  const fdrdUpcoming = [...fdrdActive].filter(t=>t.maturityDate).sort((a,b)=>new Date(a.maturityDate)-new Date(b.maturityDate)).slice(0,5);
  const fdrdMonthlyCommitment = fdrdActive.filter(t=>t.depositType==="RD").reduce((s,t)=>s+Number(t.monthlyDeposit||0),0);

  const maxBar = Math.max(...monthlyData.map(m=>Math.max(m.inc,m.exp)),1);

  const tabs = [{id:"finance",l:"💰 Finance"},{id:"lending",l:"🤝 Lending"},{id:"trading",l:"📈 Trading"},{id:"goldsilver",l:"🪙 Gold/Silver"},{id:"fdrd",l:"🏦 FD/RD"}];

  return (
    <div style={{padding:"16px 16px 0"}}>

      {/* Tab bar */}
      <div style={{display:"flex",gap:8,marginBottom:18,background:tm.card,borderRadius:16,padding:6}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{flex:1,padding:"9px 4px",borderRadius:12,border:"none",cursor:"pointer",
              fontFamily:"inherit",fontWeight:800,fontSize:12,
              background:tab===t.id?"#4F46E5":"transparent",
              color:tab===t.id?"white":tm.textSub}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* ── FINANCE TAB */}
      {tab==="finance" && <>
        {/* Summary cards */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"Income",v:fmt(totalInc),c:"#16A34A"},
            {l:"Expense",v:fmt(totalExp),c:"#DC2626"},
            {l:"Savings",v:fmt(savings),c:savings>=0?"#4F46E5":"#DC2626"},
          ].map(s=>(
            <div key={s.l} style={{background:tm.card,borderRadius:16,padding:"12px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
              <div style={{fontSize:10,color:tm.textSub,fontWeight:700,marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:14,fontWeight:900,color:s.c}}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Monthly bar chart */}
        <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:16}}>📅 Last 6 Months</div>
          <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120}}>
            {monthlyData.map(m=>(
              <div key={m.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                <div style={{width:"100%",display:"flex",gap:2,alignItems:"flex-end",height:90}}>
                  <div style={{flex:1,background:"#DCFCE7",borderRadius:"4px 4px 0 0",
                    height:`${(m.inc/maxBar)*90}px`,minHeight:2,transition:"height 0.5s"}}/>
                  <div style={{flex:1,background:"#FEE2E2",borderRadius:"4px 4px 0 0",
                    height:`${(m.exp/maxBar)*90}px`,minHeight:2,transition:"height 0.5s"}}/>
                </div>
                <div style={{fontSize:10,color:tm.textSub,fontWeight:700}}>{m.label}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,marginTop:8}}>
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:tm.textSub}}>
              <div style={{width:10,height:10,background:"#DCFCE7",borderRadius:3}}/> Income
            </div>
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:tm.textSub}}>
              <div style={{width:10,height:10,background:"#FEE2E2",borderRadius:3}}/> Expense
            </div>
          </div>
        </div>

        {/* Category breakdown */}
        {catEntries.length>0 && (
          <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:14}}>🗂️ Top Expense Categories</div>
            {catEntries.slice(0,6).map(([cat,amt])=>{
              const pct = catTotal>0?(amt/catTotal)*100:0;
              return (
                <div key={cat} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:13,fontWeight:700,color:tm.text}}>{cat}</span>
                    <span style={{fontSize:13,fontWeight:800,color:"#DC2626"}}>{fmtFull(amt)}</span>
                  </div>
                  <div style={{height:6,background:tm.border,borderRadius:999}}>
                    <div style={{height:"100%",width:`${pct}%`,background:"#4F46E5",borderRadius:999}}/>
                  </div>
                  <div style={{fontSize:10,color:tm.textSub,marginTop:2,textAlign:"right",fontWeight:600}}>{pct.toFixed(1)}%</div>
                </div>
              );
            })}
          </div>
        )}

        {catEntries.length===0 && allStdTxns.length===0 && (
          <div style={{textAlign:"center",padding:"40px 20px",background:tm.card,borderRadius:20}}>
            <div style={{fontSize:36,marginBottom:8}}>📊</div>
            <div style={{color:tm.textSub,fontWeight:700}}>No finance data yet</div>
            <div style={{color:tm.textMuted,fontSize:12,marginTop:4}}>Add transactions to see reports</div>
          </div>
        )}
      </>}

      {/* ── LENDING TAB */}
      {tab==="lending" && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"💸 Receivable",v:fmt(outstanding),c:"#DC2626",bg:"#FEE2E2"},
            {l:"💳 Payable",v:fmt(youOwe),c:"#7C3AED",bg:"#F3E8FF"},
            {l:"📤 Lent Out",v:fmt(totalLent),c:"#D97706",bg:"#FEF3C7"},
            {l:"📥 Returned",v:fmt(totalRet),c:"#16A34A",bg:"#DCFCE7"},
            {l:"⬅️ Borrowed",v:fmt(totalBorr),c:"#7C3AED",bg:"#F5F3FF"},
            {l:"✅ Repaid",v:fmt(totalRepaid),c:"#0284C7",bg:"#E0F2FE"},
            {l:"⚖️ Net Position",v:(netLendingPosition>=0?"+":"−") + fmt(netLendingPosition),c:netLendingPosition>=0?"#059669":"#DC2626",bg:netLendingPosition>=0?"#DCFCE7":"#FEE2E2"},
            {l:"🎁 Profit Amount",v:fmt(totalLendingCashback),c:"#7C3AED",bg:"#F5F3FF"},
          ].map(s=>(
            <div key={s.l} className="report-metric-card" style={{background:s.bg,borderRadius:16,padding:"14px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",border:dark?"1px solid rgba(148,163,184,.16)":"1px solid rgba(226,232,240,.65)"}}>
              <div style={{fontSize:11,color:s.c,fontWeight:700,marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
            </div>
          ))}
        </div>

        <div style={{background:dark?"rgba(15,23,42,.72)":"#FFFFFF",border:dark?"1px solid rgba(148,163,184,.18)":"1px solid #E8EDFF",borderRadius:18,padding:14,marginBottom:14,boxShadow:dark?"0 12px 24px rgba(0,0,0,.18)":"0 10px 24px rgba(15,23,42,.05)"}}>
          <div style={{fontSize:13,fontWeight:900,color:tm.text,marginBottom:8}}>🧮 Auto-calculated lending status</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div style={{background:dark?"rgba(220,38,38,.12)":"#FEF2F2",borderRadius:14,padding:"10px 12px"}}>
              <div style={{fontSize:10,fontWeight:900,color:"#DC2626",textTransform:"uppercase"}}>Owed to you</div>
              <div style={{fontSize:14,fontWeight:900,color:"#DC2626",marginTop:3}}>{receivableStatus}</div>
              <div style={{fontSize:11,fontWeight:700,color:tm.textSub,marginTop:3}}>Lent Out − Returned</div>
            </div>
            <div style={{background:dark?"rgba(124,58,237,.12)":"#F5F3FF",borderRadius:14,padding:"10px 12px"}}>
              <div style={{fontSize:10,fontWeight:900,color:"#7C3AED",textTransform:"uppercase"}}>You owe</div>
              <div style={{fontSize:14,fontWeight:900,color:"#7C3AED",marginTop:3}}>{payableStatus}</div>
              <div style={{fontSize:11,fontWeight:700,color:tm.textSub,marginTop:3}}>Borrowed − Repaid</div>
            </div>
          </div>
        </div>

        {totalLendingCashback > 0 && (
          <div style={{background:dark?"rgba(124,58,237,.13)":"#F5F3FF",border:dark?"1px solid rgba(196,181,253,.24)":"1px solid #DDD6FE",borderRadius:18,padding:14,marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:900,color:dark?"#C4B5FD":"#5B21B6"}}>🎁 Profit Amount</div>
            <div style={{fontSize:22,fontWeight:900,color:dark?"#DDD6FE":"#65A30D",marginTop:4}}>{fmtFull(totalLendingCashback)}</div>
            <div style={{fontSize:11,fontWeight:700,color:tm.textSub,marginTop:4}}>Tracked separately. Not included in lent, returned, outstanding, borrowed, repaid, or you-owe calculations.</div>
          </div>
        )}


        {lendingPendingRows.length > 0 && (
          <div style={{background:tm.card,borderRadius:20,padding:16,marginBottom:14,border:dark?"1px solid rgba(148,163,184,.16)":"1px solid rgba(226,232,240,.8)",boxShadow:dark?"0 14px 30px rgba(0,0,0,.20)":"0 12px 28px rgba(15,23,42,.06)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:12}}>
              <div>
                <div style={{fontSize:14,fontWeight:900,color:tm.text}}>📌 Pending by Profile</div>
                <div style={{fontSize:11,fontWeight:700,color:tm.textSub,marginTop:2}}>People/profiles with amount still pending</div>
              </div>
              <div style={{fontSize:11,fontWeight:900,color:"#059669",background:dark?"rgba(5,150,105,.16)":"#DCFCE7",borderRadius:999,padding:"6px 10px"}}>
                {lendingPendingRows.length} active
              </div>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {lendingPendingRows.map(row => {
                const isCollect = row.toCollect >= row.toPay;
                const mainColor = isCollect ? "#DC2626" : "#7C3AED";
                const accentBg = dark ? `${mainColor}22` : `${mainColor}12`;
                return (
                  <div key={row.key} style={{display:"grid",gridTemplateColumns:"auto 1fr auto",gap:12,alignItems:"center",background:dark?"rgba(15,23,42,.72)":"#F8FAFF",border:`1.5px solid ${dark?"rgba(148,163,184,.18)":"#E8EDFF"}`,borderRadius:16,padding:"12px"}}>
                    <div style={{width:42,height:42,borderRadius:14,background:(row.color||"#059669")+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:21,boxShadow:"inset 0 0 0 1px rgba(255,255,255,.14)"}}>{row.icon}</div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:900,color:tm.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{row.name}</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"6px 10px",marginTop:5}}>
                        {row.toCollect > 0 && <span style={{fontSize:11,fontWeight:900,color:"#DC2626"}}>To collect {fmtFull(row.toCollect)}</span>}
                        {row.toPay > 0 && <span style={{fontSize:11,fontWeight:900,color:"#7C3AED"}}>You owe {fmtFull(row.toPay)}</span>}
                      </div>
                      <div style={{fontSize:10.5,fontWeight:700,color:tm.textSub,marginTop:5}}>
                        Lent {fmtFull(row.lent)} · Returned {fmtFull(row.returned)} · Borrowed {fmtFull(row.borrowed)} · Repaid {fmtFull(row.repaid)}{row.cashback > 0 ? ` · Profit amount ${fmtFull(row.cashback)}` : ""}{row.latestDueDate ? ` · Due ${row.latestDueDate}` : ""}
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:7}}>
                      <div style={{fontSize:13,fontWeight:900,color:mainColor,background:accentBg,borderRadius:999,padding:"5px 9px"}}>
                        {isCollect ? fmtFull(row.toCollect) : fmtFull(row.toPay)}
                      </div>
                      {row.phone && (
                        <button onClick={()=>openWhatsAppReminder(row)} style={{border:"none",borderRadius:999,padding:"6px 9px",background:"#059669",color:"white",fontSize:10.5,fontWeight:900,fontFamily:"inherit",cursor:"pointer"}}>
                          WhatsApp
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Overdue alert */}
        {overdue.length > 0 && (
          <div style={{background:"#FEF2F2",border:"2px solid #FECACA",borderRadius:16,padding:14,marginBottom:14}}>
            <div style={{fontSize:14,fontWeight:800,color:"#DC2626",marginBottom:8}}>⚠️ Overdue ({overdue.length})</div>
            {overdue.map(t=>(
              <div key={t.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #FECACA"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#1E1B4B"}}>{t.person||"Unknown"}</div>
                  <div style={{fontSize:11,color:"#DC2626"}}>Due: {t.dueDate}</div>
                </div>
                <div style={{fontWeight:900,color:"#DC2626"}}>{fmt(t.amount)}</div>
              </div>
            ))}
          </div>
        )}

        {allLendTxns.length===0 && (
          <div style={{textAlign:"center",padding:"40px 20px",background:tm.card,borderRadius:20}}>
            <div style={{fontSize:36,marginBottom:8}}>🤝</div>
            <div style={{color:tm.textSub,fontWeight:700}}>No lending data yet</div>
          </div>
        )}
      </>}

      {/* ── TRADING TAB */}
      {tab==="trading" && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"🏆 Win Rate",v:`${winRate}%`,c:winRate>=50?"#16A34A":"#DC2626",bg:winRate>=50?"#DCFCE7":"#FEE2E2"},
            {l:"💰 Net P&L",v:(netPnL>=0?"+":"-")+fmt(netPnL),c:netPnL>=0?"#16A34A":"#DC2626",bg:netPnL>=0?"#DCFCE7":"#FEE2E2"},
            {l:"📈 Avg Profit",v:fmt(avgProfit),c:"#16A34A",bg:"#F0FDF4"},
            {l:"📉 Avg Loss",v:fmt(avgLoss),c:"#DC2626",bg:"#FFF5F5"},
            {l:"⚖️ Risk/Reward",v:rr,c:"#4F46E5",bg:"#EEF2FF"},
            {l:"🎯 Total Trades",v:tradeProfits.length+tradeLosses.length,c:tm.text,bg:tm.cardAlt},
          ].map(s=>(
            <div key={s.l} className="report-metric-card" style={{background:s.bg,borderRadius:16,padding:"14px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",border:dark?"1px solid rgba(148,163,184,.16)":"1px solid rgba(226,232,240,.65)"}}>
              <div style={{fontSize:11,color:tm.textSub,fontWeight:700,marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
            </div>
          ))}
        </div>

        {bestTrade && (
          <div style={{background:"#F0FDF4",border:"1.5px solid #86EFAC",borderRadius:16,padding:14,marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:800,color:"#16A34A",marginBottom:6}}>🏆 Best Trade</div>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:13,color:tm.text,fontWeight:700}}>{bestTrade.note||"Trade"}</span>
              <span style={{fontWeight:900,color:"#16A34A"}}>+{fmt(bestTrade.amount)}</span>
            </div>
          </div>
        )}
        {worstTrade && (
          <div style={{background:"#FFF5F5",border:"1.5px solid #FCA5A5",borderRadius:16,padding:14,marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:800,color:"#DC2626",marginBottom:6}}>📉 Worst Trade</div>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:13,color:tm.text,fontWeight:700}}>{worstTrade.note||"Trade"}</span>
              <span style={{fontWeight:900,color:"#DC2626"}}>-{fmt(worstTrade.amount)}</span>
            </div>
          </div>
        )}

        {allTradeTxns.length===0 && (
          <div style={{textAlign:"center",padding:"40px 20px",background:tm.card,borderRadius:20}}>
            <div style={{fontSize:36,marginBottom:8}}>📈</div>
            <div style={{color:tm.textSub,fontWeight:700}}>No trading data yet</div>
          </div>
        )}
      </>}

      {/* ── GOLD / SILVER TAB */}
      {tab==="goldsilver" && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"Remaining Holding Cost",v:fmtFull(totalGoldSilverHolding),c:"#0F766E",bg:"#CCFBF1"},
            {l:"Gold Holding Cost",v:fmtFull(metalStats.find(m=>m.metal==="Gold")?.holdingCost||0),c:"#CA8A04",bg:"#FEF3C7"},
            {l:"Silver Holding Cost",v:fmtFull(metalStats.find(m=>m.metal==="Silver")?.holdingCost||0),c:"#64748B",bg:"#F1F5F9"},
            {l:"Gold Current Avg Cost/g",v:fmtFull(metalStats.find(m=>m.metal==="Gold")?.avg||0),c:"#CA8A04",bg:"#FFFBEB"},
            {l:"Silver Current Avg Cost/g",v:fmtFull(metalStats.find(m=>m.metal==="Silver")?.avg||0),c:"#64748B",bg:"#F8FAFC"},
            {l:"Sold Value",v:fmtFull(totalGoldSilverSold),c:"#2563EB",bg:"#DBEAFE"},
            {l:"Cashback",v:fmtFull(totalGoldSilverCashback),c:"#7C3AED",bg:"#F5F3FF"},
            {l:"Gold Realized P/L",v:((metalStats.find(m=>m.metal==="Gold")?.realised||0)>=0?"+":"")+fmtFull(metalStats.find(m=>m.metal==="Gold")?.realised||0),c:(metalStats.find(m=>m.metal==="Gold")?.realised||0)>=0?"#16A34A":"#EF4444",bg:"#F0FDF4"},
            {l:"Silver Realized P/L",v:((metalStats.find(m=>m.metal==="Silver")?.realised||0)>=0?"+":"")+fmtFull(metalStats.find(m=>m.metal==="Silver")?.realised||0),c:(metalStats.find(m=>m.metal==="Silver")?.realised||0)>=0?"#16A34A":"#EF4444",bg:"#F8FAFC"},
          ].map(s=>(
            <div key={s.l} className="report-metric-card" style={{background:s.bg,borderRadius:16,padding:"14px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",border:dark?"1px solid rgba(148,163,184,.16)":"1px solid rgba(226,232,240,.65)"}}>
              <div style={{fontSize:11,color:s.c,fontWeight:700,marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
            </div>
          ))}
        </div>
        <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:14}}>🪙 Holdings</div>
          {metalStats.map(m=>(
            <div key={m.metal} style={{padding:"12px 0",borderBottom:`1px solid ${tm.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:14,fontWeight:900,color:tm.text}}>{m.metal === "Gold" ? "🥇" : "🥈"} {m.metal}</span>
                <span style={{fontSize:14,fontWeight:900,color:m.metal==="Gold"?"#CA8A04":"#64748B"}}>{fmtGram(m.holding)}</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12,color:tm.textSub,fontWeight:700}}>
                <div>Current avg cost/g: {fmtFull(m.avg)}</div>
                <div>Sold: {fmtGram(m.sold)}</div>
                <div>Purchase value: {fmtFull(m.netCost)}</div>
                <div>Holding cost: {fmtFull(m.holdingCost)}</div>
                <div>Cashback received: {fmtFull(m.cashback)}</div>
                <div>Realized P/L: {(m.realised>=0?"+":"")+fmtFull(m.realised)}</div>
              </div>
            </div>
          ))}
          <div style={{fontSize:11,color:tm.textSub,fontWeight:700,lineHeight:1.5,marginTop:12}}>Tracking only. FinVault does not buy/sell gold or silver and does not provide investment advice.</div>
        </div>
        {allGoldTxns.length===0 && (
          <div style={{textAlign:"center",padding:"40px 20px",background:tm.card,borderRadius:20}}>
            <div style={{fontSize:36,marginBottom:8}}>🪙</div>
            <div style={{color:tm.textSub,fontWeight:700}}>No gold/silver data yet</div>
            <div style={{color:tm.textMuted,fontSize:12,marginTop:4}}>Create a Gold/Silver profile and add buy/sell entries.</div>
          </div>
        )}
      </>}


      {/* ── FD / RD TAB */}
      {tab==="fdrd" && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"Invested",v:fmt(fdrdInvested),c:"#0F766E",bg:"#ECFDF5"},
            {l:"Expected Return",v:`${fdrdExpectedPct.toFixed(2)}%`,c:"#4F46E5",bg:"#EEF2FF"},
            {l:"Actual Returned",v:fmt(fdrdActual),c:"#7C3AED",bg:"#F5F3FF"},
            {l:"Actual Return",v:`${fdrdActualPct.toFixed(2)}%`,c:"#EA580C",bg:"#FFF7ED"},
          ].map(s=>(
            <div key={s.l} className="report-metric-card" style={{background:s.bg,borderRadius:16,padding:"14px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",border:dark?"1px solid rgba(148,163,184,.16)":"1px solid rgba(226,232,240,.65)"}}>
              <div style={{fontSize:11,color:s.c,fontWeight:700,marginBottom:4}}>{s.l}</div>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
            </div>
          ))}
        </div>
        <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:14}}>🏦 FD/RD Summary</div>
          {[
            ["Expected maturity", fmtFull(fdrdExpected)],
            ["Expected gain", fmtFull(fdrdExpectedGain)],
            ["Actual gain", fmtFull(fdrdActualGain)],
            ["Penalty / charges", fmtFull(fdrdPenalty)],
            ["Active deposits", String(fdrdActive.length)],
            ["Closed deposits", String(fdrdClosed.length)],
            ["Monthly RD commitment", fmtFull(fdrdMonthlyCommitment)],
          ].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${tm.border}`}}>
              <span style={{fontSize:13,fontWeight:800,color:tm.textSub}}>{l}</span>
              <span style={{fontSize:13,fontWeight:900,color:tm.text}}>{v}</span>
            </div>
          ))}
          <div style={{fontSize:11,color:tm.textSub,fontWeight:700,lineHeight:1.5,marginTop:12}}>Tracking only. FinVault does not provide banking, deposit, tax, or investment advice.</div>
        </div>
        {!!fdrdUpcoming.length && <div style={{background:tm.card,borderRadius:20,padding:18,marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:800,color:tm.text,marginBottom:14}}>⏰ Upcoming Maturity</div>
          {fdrdUpcoming.map(t=>(
            <div key={t.id} style={{padding:"12px 0",borderBottom:`1px solid ${tm.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:10,marginBottom:4}}>
                <span style={{fontSize:14,fontWeight:900,color:tm.text}}>{t.depositName || t.bankName || t.depositType}</span>
                <span style={{fontSize:13,fontWeight:900,color:"#0F766E"}}>{fmtFull(t.expectedMaturityAmount || t.amount || 0)}</span>
              </div>
              <div style={{fontSize:12,color:tm.textSub,fontWeight:700}}>{t.depositType || "FD"} · {t.interestRate || 0}% · Maturity {t.maturityDate}</div>
            </div>
          ))}
        </div>}
        {allFdrdTxns.length===0 && (
          <div style={{textAlign:"center",padding:"40px 20px",background:tm.card,borderRadius:20}}>
            <div style={{fontSize:36,marginBottom:8}}>🏦</div>
            <div style={{color:tm.textSub,fontWeight:700}}>No FD/RD data yet</div>
            <div style={{color:tm.textMuted,fontSize:12,marginTop:4}}>Create an FD/RD profile and add your first deposit record.</div>
          </div>
        )}
      </>}

    </div>
  );
}
